import { useCallback, useEffect, useState } from 'react';
import { Modal } from '../components/Modal';
import {
    api,
    ApiError,
    newIdempotencyKey,
    type PiggyPickResult,
    type PiggyRecent,
    type PiggyRound,
} from '../lib/api';
import type { ViewId } from '../state';

type Props = {
    onNavigate: (id: ViewId) => void;
    onBalanceChanged?: (balance: number) => void;
};

const PEN_EMOJI = '🐖';

/**
 * PiggyBank.md — 30 players × 9 pens. Player picks one pen, pays a fixed
 * stake, and wins a share of the pot if their pen ends up being the least
 * popular non-empty one. Half of the losers' stakes goes to the house, the
 * other half is split equally among winners (who also get their stake back).
 *
 * Concurrency / replay: server is the source of truth. We poll the open round
 * every 5s while we don't have an unresolved pick of our own, and replace the
 * UI with the result popup as soon as the 30th pick lands.
 */
export function PiggyBankView({ onNavigate, onBalanceChanged }: Props) {
    const [round, setRound] = useState<PiggyRound | null>(null);
    const [recent, setRecent] = useState<PiggyRecent[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [outcome, setOutcome] = useState<PiggyPickResult | null>(null);

    const reload = useCallback(async () => {
        try {
            const r = await api.getPiggy();
            setRound(r.round);
            setRecent(r.recent);
        } catch (e) {
            setError(e instanceof ApiError ? `${e.status}: ${e.code}` : 'Не удалось загрузить раунд.');
        }
    }, []);

    useEffect(() => { void reload(); }, [reload]);

    /* Poll while waiting for other players to fill the round. Stops once the
     * caller has picked and the round is full (we'd just be re-fetching the
     * same finished snapshot). */
    useEffect(() => {
        if (outcome?.finished) return;
        const id = window.setInterval(() => { void reload(); }, 5000);
        return () => window.clearInterval(id);
    }, [reload, outcome]);

    const pick = async (pen: number) => {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            const res = await api.piggyPick(pen, newIdempotencyKey());
            setOutcome(res);
            onBalanceChanged?.(res.balance);
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? translate(e.code) : 'Не удалось сделать выбор.');
        } finally {
            setBusy(false);
        }
    };

    const closeOutcome = () => {
        setOutcome(null);
        void reload();
    };

    const stake = round?.stake ?? 25;
    const joined = round?.joined ?? 0;
    const max = round?.maxPlayers ?? 30;
    const myPen = round?.myPen ?? null;

    return (
        <div className="view">
            <h2 className="text-center fw-900" style={{ fontSize: 18, letterSpacing: 2 }}>
                PIGGY BANK
            </h2>
            <div className="text-center text-muted f-12" style={{ marginTop: -6 }}>
                {joined}/{max} участников
            </div>

            <div className="piggy-grid">
                {Array.from({ length: 9 }, (_, i) => i + 1).map((pen) => {
                    const mine = myPen === pen;
                    return (
                        <button
                            key={pen}
                            type="button"
                            className={`piggy-cell${mine ? ' selected' : ''}`}
                            onClick={() => !myPen && !busy && pick(pen)}
                            disabled={!!myPen || busy}
                            aria-pressed={mine}
                            aria-label={`Свинка номер ${pen}`}
                        >
                            <span className="piggy-emoji">{PEN_EMOJI}</span>
                            <span className="piggy-num">{pen}</span>
                        </button>
                    );
                })}
            </div>

            {myPen
                ? (
                    <div className="info-card" style={{ borderColor: 'var(--accent)' }}>
                        <p className="popup-text">
                            Вы выбрали свинку <b>#{myPen}</b>. Ждём остальных участников.
                        </p>
                    </div>
                )
                : (
                    <button
                        type="button"
                        className="action-btn btn-red"
                        disabled
                        style={{ opacity: 0.7 }}
                    >
                        ИГРАТЬ — {stake} BSP
                    </button>
                )}

            <div className="info-card">
                <p className="popup-text">
                    <b>Правила.</b> Выберите свинку, которую выберет меньше всего людей.
                    Когда раунд заканчивается — выбирается та свинка, которую выбрали меньше всего раз
                    (но не пустая). Те, кто ставил на другие свинки — проигрывают. Победители делят
                    между собой половину ставок проигравших поровну. Цена входа {stake} BSP.
                </p>
            </div>

            {error && (
                <div className="info-card" style={{ borderColor: 'var(--red)' }}>
                    <p className="popup-text" style={{ color: 'var(--red)' }}>{error}</p>
                </div>
            )}

            <h3 className="text-muted fw-900 f-12" style={{ textTransform: 'uppercase' }}>
                Последние раунды
            </h3>
            <div className="info-card" style={{ padding: '4px 12px' }}>
                {recent.length === 0 && (
                    <div className="text-muted f-12 text-center" style={{ padding: 12 }}>
                        Ещё нет завершённых раундов.
                    </div>
                )}
                {recent.map((r) => (
                    <div key={r.id} className="top-row">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <span className="top-name fw-900" style={{ fontSize: 11 }}>
                                Раунд #{r.id} · банк {r.pot} BSP
                            </span>
                            <span className="text-muted f-10">
                                Победные свинки: {r.winnersPens.length === 0 ? '—' : r.winnersPens.join(', ')}
                            </span>
                        </div>
                        <span className="fw-900 f-12" style={{ color: 'var(--green)' }}>
                            +{r.perWinner} BSP
                        </span>
                    </div>
                ))}
            </div>

            <button type="button" className="back-btn" onClick={() => onNavigate('games')}>
                НАЗАД В ИГРЫ
            </button>

            {outcome && outcome.finished && (
                <Modal title={outcome.won ? 'Победа' : 'Результат'} onClose={closeOutcome}>
                    <div className="text-center" style={{ fontSize: 38 }}>
                        {outcome.won ? '🐷💰' : '😢'}
                    </div>
                    <p className="popup-text text-center">
                        {outcome.won
                            ? `Вы выбрали свинку #${outcome.pen} — самая непопулярная. Получено ${outcome.payout} BSP.`
                            : `Победные свинки: ${(outcome.winnerPens ?? []).join(', ') || '—'}.`}
                    </p>
                    <button type="button" className="action-btn btn-red" onClick={closeOutcome}>
                        ОК
                    </button>
                </Modal>
            )}
            {outcome && !outcome.finished && (
                <Modal title="Свинка зарезервирована" onClose={closeOutcome}>
                    <div className="text-center" style={{ fontSize: 32 }}>⏳</div>
                    <p className="popup-text text-center">
                        Свинка #{outcome.pen} зарезервирована. Ждём ещё {(outcome.maxPlayers ?? max) - (outcome.joined ?? joined)} человек.
                    </p>
                    <button type="button" className="action-btn btn-outline-gold" onClick={closeOutcome}>
                        ХОРОШО
                    </button>
                </Modal>
            )}
        </div>
    );
}

function translate(code: string) {
    switch (code) {
        case 'insufficient_balance': return 'Недостаточно BSP.';
        case 'already_picked':      return 'Вы уже сделали выбор в этом раунде.';
        case 'round_full':          return 'Раунд только что заполнился. Откройте следующий.';
        case 'too_many_requests':   return 'Слишком часто. Подождите пару секунд.';
        default:                    return 'Не удалось сделать выбор (' + code + ').';
    }
}
